/**
 * Wrecks of the expansion's vehicles (10 §2): what missions 14, 17, 20 and 29 leave behind. As in
 * wrecks.ts, each keeps the one shape that made the thing recognisable - cab and bonnet, the tank's
 * hoops, the missile, the bow - burnt, broken, and inside the old footprint, in the prop's own frame.
 */

import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import { BARGE_DECK_Y, BARGE_HULL } from './boats';
import { LAUNCHER_MISSILE } from './convoy';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { tubeZ, wheel } from './parts';
import { HALF_GAUGE, RAIL_TOP, RAIL_WHEEL_DROP, trackBed } from './rail';
import { prism } from './shapes';
import { burnt, debris, scorch } from './wreckParts';

/** Shunter: off the rails and rolled toward the east, bonnet burst over the engine, cab gutted, roof fallen in. */
export const locomotiveWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 4.1, 9.2);
  trackBed(mb, p, 10);
  shunterHulk(mb, p, rng, 0);
  return mb;
};

/**
 * The train under way, wrecked (ROUND4 §1, `EnemyKind` `railEngine`): the same hulk with no track
 * under it, dropped by the same `RAIL_WHEEL_DROP` its live model is, so wreck and vehicle line up.
 */
export const railEngineWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 4.1, 9.2);
  shunterHulk(mb, p, rng, RAIL_WHEEL_DROP);
  return mb;
};

/** The burnt shunter itself, `drop` metres below the frame it is built in (the scorch stays on the ground). */
function shunterHulk(mb: MeshBuilder, p: Palette, rng: Pcg32, drop: number): void {
  mb.with(place(0.12, 0.2 - drop, 0.2, 0.05, 1, 0, -0.12), () => {
    const deck = 1.1;
    mb.box(2.7, 0.28, 8.8, burnt(p), place(0, deck - 0.28, 0));
    mb.box(2.6, 0.45, 0.2, p.color('enemyAccent.0'), place(0, 0.6, 4.4));
    // What is left of the bonnet: the nose stands, the middle is an open, blackened engine bay.
    mb.box(2.0, 1.4, 1.9, burnt(p), place(0, deck, 3.1));
    mb.box(2.0, 0.7, 3.1, { all: p.color('ink.1'), top: p.color('ink.0') }, place(0, deck, 0.6));
    mb.box(0.9, 0.5, 2.2, p.color('metal.lo'), place(0.1, deck + 0.7, 0.6, 0.1, 1, 0.1));
    mb.box(2.2, 0.08, 1.8, burnt(p), place(-0.5, deck + 1.1, 0.9, 0.3, 1, 0.2, 0.7));
    // Cab: four walls with no glass, its roof dropped into it; the short bonnet behind.
    const gutted = { all: p.color('ink.1'), top: p.color('ink.0'), front: p.color('ink.0') };
    mb.box(2.6, 1.6, 2.2, gutted, place(0, deck, -2.05));
    mb.box(2.8, 0.14, 2.4, burnt(p), place(0.1, deck + 1.3, -2.0, 0.1, 1, 0.3, -0.2));
    mb.box(2.0, 1.0, 1.25, burnt(p), place(0, deck, -3.75));
    for (const z of [-2.0, 0, 2.0]) wheel(mb, p, [-HALF_GAUGE, RAIL_TOP + 0.25, z], 0.52, 0.16, 6, 'ink.0');
  });
  debris(mb, p, rng, 6, 1.5, 4.4);
}

/** Tank wagon: frame sagged in the middle, the tank burst into two shells peeled apart, one red hoop left. */
export const fuelWagonWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 4.0, 7.8);
  trackBed(mb, p, 8);
  const yaw = rng.range(-0.08, 0.08);
  for (const end of [-1, 1]) {
    mb.with(place(0, 0.62, end * 1.8, yaw, 1, end * -0.13), () => {
      mb.box(2.5, 0.22, 3.6, burnt(p));
      wheel(mb, p, [-HALF_GAUGE, 0.12, end * 0.5], 0.45, 0.16, 6, 'ink.0');
      // Half the tank, its torn end gaping toward the middle of the wagon.
      const z0 = end > 0 ? -0.9 : -1.6;
      tubeZ(mb, end * 0.15, 1.1, z0, z0 + 2.5, 0.88, 0.88, 8, p.color('ink.1'), p.color('ink.0'));
      if (end > 0) tubeZ(mb, 0.15, 1.1, 0.4, 0.7, 0.92, 0.92, 8, p.color('enemyAccent.0'));
    });
  }
  // Petals of tank skin standing up out of the break.
  for (const sx of [-1, 1]) {
    mb.box(0.08, 1.5, 1.3, burnt(p), place(sx * 0.7, 0.8, rng.range(-0.3, 0.3), 0, 1, 0, sx * -0.6));
  }
  debris(mb, p, rng, 6, 1.4, 3.6);
  return mb;
};

/** Launcher: back broken, cabs crushed, tyres burnt off - and the missile snapped, its warm nose across the hull. */
export const mobileLauncherWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const m = LAUNCHER_MISSILE;
  scorch(mb, p, 4.0, 8.8);
  for (const end of [-1, 1]) {
    mb.with(place(0, 0.3, end * 2.0, rng.range(-0.06, 0.06), 1, end * -0.1), () => {
      mb.box(2.9, 0.75, 4.0, burnt(p));
      for (const sx of [-1, 1]) wheel(mb, p, [sx * 1.25, 0.15, end * 1.0], 0.5, 0.4, 6, 'ink.0');
    });
  }
  for (const sx of [-1, 1]) mb.box(0.95, 0.55, 1.6, burnt(p), place(sx * 0.97, 0.75, 3.2, sx * 0.1, 1, 0.1));
  // Rear half of the missile still in its saddle, broken end cocked up; the front half fallen across the hull.
  mb.with(place(0, 1.55, -2.2, -0.05, 1, -0.16), () => {
    mb.box(0.7, 0.22, 3.4, p.color('metal.lo'), place(0, -0.6, 0));
    tubeZ(mb, 0, 0, -1.7, 1.7, m.r, m.r, 8, p.color('ink.1'), p.color('ink.0'));
    mb.box(1.5, 0.06, 0.7, p.color('metal.lo'), place(0, -0.03, -1.3));
  });
  mb.with(place(-0.55, 1.62, 0.2, rng.range(0.15, 0.3), 1, 0.17), () => {
    tubeZ(mb, 0, 0, 0, 2.5, m.r, m.r, 8, p.color('metal.mid'), p.color('ink.0'));
    tubeZ(mb, 0, 0, 2.15, 2.5, m.r + 0.03, m.r + 0.03, 8, p.color('fire.2'));
    tubeZ(mb, 0, 0, 2.5, 3.5, m.r, 0, 8, p.color('fire.0'));
  });
  debris(mb, p, rng, 7, 1.5, 4.0);
  return mb;
};

/** Staff car: a burnt shell down on its rims, bonnet blown up, roof caved, a door hanging open. */
export const staffCarWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  scorch(mb, p, 2.5, 4.8);
  mb.with(place(0, 0, 0, rng.range(-0.08, 0.08), 1, 0, 0.05), () => {
    for (const z of [-1.35, 1.35]) wheel(mb, p, [-0.78, 0.22, z], 0.26, 0.2, 6, 'ink.0');
    mb.box(1.66, 0.5, 4.2, burnt(p), place(0, 0.12, 0));
    mb.box(1.2, 0.28, 1.4, p.color('ink.0'), place(0, 0.62, 1.25));
    mb.box(1.3, 0.06, 1.4, burnt(p), place(0, 0.9, 0.55, 0, 1, -0.95));
    // Cabin: four posts and the roof they no longer hold up.
    for (const sx of [-1, 1]) {
      for (const z of [-1.3, 0.4]) mb.box(0.09, 0.5, 0.09, p.color('ink.1'), place(sx * 0.7, 0.62, z));
    }
    mb.box(1.58, 0.1, 1.95, burnt(p), place(0.05, 1.02, -0.45, 0, 1, 0.12, -0.1));
    mb.box(0.06, 0.5, 0.9, p.color('ink.1'), place(-0.86, 0.3, 0.1, -0.7));
    // The boot kept a little of its paint.
    mb.box(1.4, 0.26, 0.75, { all: p.color('ink.1'), top: p.color('uniformEnemy.0') }, place(0, 0.62, -1.7));
  });
  debris(mb, p, rng, 4, 0.7, 2.0);
  return mb;
};

/**
 * Barge: sunk by the stern. The wreck stands on the river bed, so the water lies ~0.6 m up it: the
 * deck runs under from about amidships, leaving the bow, its bollards and the tops of the cargo.
 */
export const bargeWreck: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.with(place(0, -0.5, 0.2, 0, 1, -0.17, rng.range(0.05, 0.1)), () => {
    // The hull is carried well below its keel line, so a bow cocked up over dry ground or ice shows no gap under it.
    prism(mb, BARGE_HULL, -1.2, BARGE_DECK_Y, { top: p.color('ink.1'), side: p.color('roof.0') });
    for (const sx of [-1, 1]) {
      mb.box(0.15, 0.3, 7.3, burnt(p), place(sx * 1.58, BARGE_DECK_Y, -0.1));
      mb.cylinder(0.13, 0.19, 0.42, 6, p.color('metal.lo'), place(sx * 1.55, BARGE_DECK_Y, 4.35), p.color('metal.mid'));
    }
    // The cargo where it shifted: tarpaulins scorched, one crate of the enemy's ammunition showing.
    const tarp = { all: p.color('canvas.lo'), top: p.color('canvas.mid') };
    mb.box(2.4, 0.85, 1.9, tarp, place(0.2, BARGE_DECK_Y, 1.3, 0.12));
    const charred = { all: p.color('ink.1'), top: p.color('canvas.lo') };
    mb.box(2.6, 0.95, 2.1, charred, place(-0.15, BARGE_DECK_Y, -1.4, -0.1));
    mb.box(2.2, 0.9, 1.7, tarp, place(0.3, BARGE_DECK_Y, -3.6, 0.2));
    const crate = { all: p.color('timber.lo'), top: p.color('timber.mid') };
    mb.box(1.0, 0.5, 0.7, crate, place(0.5, BARGE_DECK_Y, 3.3, 0.5));
    mb.box(1.02, 0.12, 0.72, p.color('enemyAccent.0'), place(0.5, BARGE_DECK_Y + 0.2, 3.3, 0.5));
  });
  return mb;
};
